import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { LoaderService } from 'src/app/providers/services/loader.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Mission } from 'src/app/storage/models/mission.model';
import { MobileDevice } from 'src/app/storage/models/mobile-device.model';
import { TeamMission } from 'src/app/storage/models/team-mission.model';
import { Team } from 'src/app/storage/models/team.model';

@Component({
    selector: 'app-team-config',
    templateUrl: './team-config.component.html',
    styleUrls: ['./team-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamConfigComponent implements OnInit, OnDestroy {
  mission: Mission;
  missionTeams: TeamMission[];

  rolename = CONSTANTS.TEAM.ROLES.TECHNICIAN;
  addingTeam = false;
  show: boolean = true;

  mobileDevices: MobileDevice[];

  private missionSubscription: Subscription;
  private teamsSubscription: Subscription;

  constructor(
    private navigationService: NavigationService,
    private missionsService: MissionsService,
    private teamsService: TeamsService,
    private loader: LoaderService,
    private translateService: TranslateService,
    private confirmationModalService: ConfirmationModalService
  ) { }

  ngOnInit(): void {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      this.mission = mission;

      const mobileDevices = await this.teamsService.getMobileDevices();
      
      if (mobileDevices) {
        this.mobileDevices = mobileDevices;
      } else {
        await this.backToMissionConfig();
      }

      await this.getMissionTechnicianTeams();
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
    if (this.teamsSubscription) { this.teamsSubscription.unsubscribe(); }
  }

  refresh(): void {
    this.show = false;
    setTimeout(() => this.show = true);
  }

  async getMissionTechnicianTeams(): Promise<void> {
    if (this.teamsSubscription) { this.teamsSubscription.unsubscribe(); }
    
    this.loader.show();

    const missionTechnicianTeams = await this.teamsService.getMissionTeams({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN
    });

    this.loader.hide();

    if (missionTechnicianTeams == null) {
      await this.backToMissionConfig();
    } else {
      this.teamsService.missionTeams$.next(missionTechnicianTeams);
    }

    this.teamsSubscription = this.teamsService.missionTeams$.subscribe(async (missionTeams: TeamMission[]) => {
      this.missionTeams = missionTeams;
    });
  }

  async backToMissionConfig() {
    await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_CONFIG);
  }

  isTeamComplete(): boolean {
    return this.mission.teamstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;
  }

  openTeamModal(): void {
    this.addingTeam = true;
  }

  closeTeamModal(): void {
    this.addingTeam = false;
  }

  async addTeam(teamToAdd: Team): Promise<void> {
    const teamMission = new TeamMission({
      ...teamToAdd,
      teammissionleader: 0,
      createdOn: Math.floor(Date.now() / 1000).toString(),
      createdBy: null,
      changedOn: null,
      changedBy: null
    });

    const isTeamAdded = await this.teamsService.addUpdateMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN,
      missionTeam: teamMission
    });

    if (isTeamAdded) {
      // Add team to behavior subject
      this.teamsService.missionTeams$.next(
        [...this.missionTeams, teamMission]
      );

      if (this.mission.teamstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
        // Set team status to 'IN PROGRESS'
        await this.setTeamStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
      }
    }
  }

  async removeTeam(team: TeamMission): Promise<void> {
    const isTeamRemoved = await this.teamsService.removeMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN,
      missionTeam: team
    });

    if (isTeamRemoved) {
      // Remove team from behavior subject
      this.teamsService.missionTeams$.next(
        this.missionTeams.filter(missionTeam => missionTeam.teamMemberId !== team.teamMemberId)
      );
    }
  }

  async toggleMissionLeader(teamToUpdate: TeamMission): Promise<void> {
    this.loader.show();

    if (!teamToUpdate.isMissionLeader) {
      // Remove mission team leader from other teams
      const teamsToUpdate = this.missionTeams.filter(team => !!team.isMissionLeader);
      await Promise.all(teamsToUpdate.map(async (team) => await this.updateTeamMissionLeader(team, false)));
    }
    
    // Update select mission's team
    await this.updateTeamMissionLeader(teamToUpdate, !teamToUpdate.isMissionLeader);

    this.loader.hide();
  }

  async toggleMissionApprentice(teamToUpdate: TeamMission): Promise<void> {
    this.loader.show();

    // Update select mission's team
    this.missionTeams.find(team => team.teamMemberId === teamToUpdate.teamMemberId).teammissionapprentice = !teamToUpdate.teammissionapprentice ? 1 : 0;

    await this.teamsService.addUpdateMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN,
      missionTeam: teamToUpdate
    });
    
    this.loader.hide();
  }

  async updateTeamMissionLeader(missionTeam: TeamMission, missionLeader: boolean): Promise<void> {
    const { YES, NO } = CONSTANTS.TEAM.MISSION_LEADER;
    missionTeam.teammissionleader = missionLeader ? YES : NO;

    // Update team
    const isTeamUpdated = await this.teamsService.addUpdateMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN,
      missionTeam: missionTeam
    });

    // If update failed, reset mission leader
    if (!isTeamUpdated) {
      missionTeam.teammissionleader = missionLeader ? NO : YES;
      this.refresh();
    }
    
  }

  async setMobileDevice($event, missionTeam: TeamMission): Promise<void> {
    const mobileDevicepk = $event.target.value;
    const mobileDevice = this.mobileDevices.find(_mobileDevice => _mobileDevice.mobileDevicepk === parseInt(mobileDevicepk));

    const updatedMissionTeam = new TeamMission(missionTeam);

    if (mobileDevicepk && mobileDevice) {
      updatedMissionTeam.mobileDevicepk = mobileDevice.mobileDevicepk;
      updatedMissionTeam.mobileDeviceLTEProvider = mobileDevice.mobileDeviceLTEProvider;
      updatedMissionTeam.mobileDeviceSimNumber = mobileDevice.mobileDeviceSimNumber;
    } else {
      updatedMissionTeam.mobileDevicepk = null;
      updatedMissionTeam.mobileDeviceLTEProvider = null;
      updatedMissionTeam.mobileDeviceSimNumber = null;
    }

    // Update mission team
    const isMissionTeamUpdated = await this.teamsService.addUpdateMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.TECHNICIAN,
      missionTeam: updatedMissionTeam
    });
    
    // Set mobile device if succeeded
    if (isMissionTeamUpdated) {
      missionTeam.mobileDevicepk = mobileDevice?.mobileDevicepk;
      missionTeam.mobileDeviceLTEProvider = mobileDevice?.mobileDeviceLTEProvider;
      missionTeam.mobileDeviceSimNumber = mobileDevice?.mobileDeviceSimNumber;
    }

    this.refresh();
  }

  async toggleTeamStatus(): Promise<void> {
    // New status
    const status = (this.mission.teamstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED) ?
                    CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS :
                    CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;

    // Confirm
    if (status === CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED ) {
      const translations = await this.translateService.get('TEAM').toPromise();
      let title = `<div style="font-weight:bold">${translations['STATUS_COMPLETE']}</div>`;

      // Check for additional warnings
      const locationcodes = this.missionTeams.reduce((acc, cur) => {
        return acc.find(code => code === cur.peoplelocationcode) != null ?
                acc :
                [...acc, cur.peoplelocationcode]
      }, []);

      const hasMissionLeader = this.missionTeams.find(team => team.isMissionLeader);

      const hasWarnings = (!hasMissionLeader || locationcodes.length > 1)
      
      if (hasWarnings) {
        title +=`<br><div><img src="assets/images/error-icon.svg" width="50" height="50"/></div>`
      }

      if (!hasMissionLeader) {
        title += `<br><div style="color:red">${translations['NO_MISSION_LEADER']}</div>`;
      }

      if (locationcodes.length > 1) {
        title += `<br><div style="color:red">${translations['DIFFERENT_LOCATIONS']}</div>`;
      }

      // Open confirmation modal
      this.confirmationModalService.openModal({
        title: title,
        hideControls: hasWarnings,
        buttons: [
          {
            text: await this.translateService.get('CONFIRM').toPromise(),
            method: async () => {
              this.confirmationModalService.closeModal();
              await this.setTeamStatus(status);
            }
          },
          {
            text: await this.translateService.get('CANCEL').toPromise(),
            method: async () => { this.confirmationModalService.closeModal(); }
          }
        ]
      });
    } else {
      await this.setTeamStatus(status);
    }
  }

  async setTeamStatus(status: number): Promise<void> {
    // Update team status
    const isStatusUpdated = await this.missionsService.updateMissionTeamStatus(this.mission.missionId, status);

    // If update succeeded, set mission team status
    if (isStatusUpdated) {
      this.mission.teamstatus = status;
    }
  }

  getTeamStatusIcon(): string {
    const icon = this.isTeamComplete() ? 'edit-icon' : 'complete-icon';
    return `assets/images/${icon}.svg`;
  }

}
